import { useMemo } from 'react';
import { Icon } from '@/lib/polaris';
import { XIcon } from '@/lib/polaris-icons';
import { useAuth } from '@smarty/shared';
import { TenantAvatar } from '@/components/admin/TenantAvatar';
import styles from './CabeceraDelMenuMovil.module.css';

/**
 * Cabecera del menú lateral en móvil: cerrar + qué tienda estoy mirando.
 *
 * POR QUÉ EXISTE
 * El botón de cerrar que trae `Frame` es un cuadrado suelto que Polaris planta con `position:
 * absolute` FUERA del panel, sobre el telón. En un teléfono real se leía como un botón huérfano
 * pegado al fondo oscuro, y además caía a la altura del primer ítem del menú, o sea compartiendo
 * fila con «Inicio».
 *
 * En vez de moverlo unos píxeles, el panel gana lo que tiene cualquier menú móvil serio: una
 * cabecera propia, alineada con la barra superior, donde el cerrar es el primer elemento y al lado
 * va la identidad de la tienda. Eso resuelve dos cosas de una: la ✕ deja de flotar, y el menú
 * abierto dice EN QUÉ TIENDA estás — que en multi-tenant no es decorativo.
 *
 * 🪤 SÓLO EN MÓVIL, Y POR CSS. En escritorio el panel es fijo y no se cierra: una cabecera con ✕
 * ahí sería un botón que no hace nada. Se oculta con la MISMA media query que usa `Frame` para
 * decidir si el menú es un panel deslizante (48em), no con un hook de breakpoints: así no hay dos
 * fuentes de verdad que se puedan desincronizar, ni un parpadeo en el primer render.
 *
 * 🪤 Y el `.Polaris-Frame__NavigationDismiss` nativo se esconde en `polaris.css` SÓLO donde esta
 * cabecera está presente (`:has`). Los paneles contextuales —Chats, Llamadas, Ads, Agentes— no la
 * llevan y siguen necesitando su ✕; apagarla para todos los dejaría sin forma de cerrar.
 */
export function CabeceraDelMenuMovil({ onDismiss }: { onDismiss?: () => void }) {
  const { activeTenant, tenantId, storeSlug } = useAuth();

  /*
   * Misma resiliencia que el selector de tiendas del topbar: durante el arranque la lista de
   * tenants todavía no llegó, pero el tenant del path sí. Sin esto la cabecera aparecería vacía
   * justo en el primer segundo, que es cuando más se la mira.
   */
  const tienda = useMemo(() => {
    if (activeTenant) return { id: activeTenant.id, name: activeTenant.name };
    if (tenantId) return { id: tenantId, name: storeSlug ?? 'Mi tienda' };
    return null;
  }, [activeTenant, tenantId, storeSlug]);

  /*
   * `data-cabecera-menu-movil` es el gancho que usa `polaris.css` para apagar la ✕ nativa justo en
   * los paneles que llevan esta cabecera: el nombre de la clase lo genera el módulo CSS con un hash
   * y no se puede nombrar desde una hoja de estilos global.
   */
  return (
    <div className={styles.cabecera} data-cabecera-menu-movil>
      <button
        type="button"
        className={styles.cerrar}
        onClick={onDismiss}
        aria-label="Cerrar el menú"
      >
        <Icon source={XIcon} />
      </button>
      {tienda && (
        <>
          <TenantAvatar id={tienda.id} name={tienda.name} size={24} />
          <span className={styles.nombre}>{tienda.name}</span>
        </>
      )}
    </div>
  );
}
